import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Compass, FileText, Bell, Loader2, ArrowUpRight, CheckCircle2 } from "lucide-react";

export default function CandidateDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [interests, setInterests] = useState([]);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    api.get("/me/candidate").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/me/interests").then(({ data }) => setInterests(data)).catch(() => {});
    api.get("/me/notifications").then(({ data }) => setNotes(data)).catch(() => {});
  }, []);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setNotes((n) => n.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
  };

  if (!profile) return <div className="h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const fields = ["headline", "location", "linkedin_url", "summary", "resume_file_url"];
  const filled = fields.filter((f) => profile[f]).length;
  const pct = Math.round((filled / fields.length) * 100);

  return (
    <div>
      <PageHeader overline={`Welcome, ${user?.full_name?.split(" ")[0] || ""}`} title="Candidate Dashboard" />
      <div className="p-8 grid lg:grid-cols-3 gap-px bg-black/10">
        <div className="lg:col-span-2 space-y-px">
          <div className="bg-white p-6">
            <div className="flex items-center justify-between mb-3">
              <p className="overline">Profile strength</p>
              <span className="font-heading font-black text-2xl">{pct}%</span>
            </div>
            <div className="h-2 bg-black/10"><div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} /></div>
            <button onClick={() => navigate("/candidate/profile")} className="mt-4 inline-flex items-center gap-1 text-sm text-primary hover:gap-2 transition-all">
              Complete your profile <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-px">
            <button onClick={() => navigate("/candidate/opportunities")} className="group bg-white p-6 text-left hover:bg-[#F1F1F3] transition-colors">
              <Compass className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
              <p className="font-heading font-bold mt-4">Browse opportunities</p>
              <p className="text-sm text-muted-foreground">See open & featured roles</p>
            </button>
            <button onClick={() => navigate("/candidate/applications")} className="group bg-white p-6 text-left hover:bg-[#F1F1F3] transition-colors">
              <FileText className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" strokeWidth={1.5} />
              <p className="font-heading font-black text-3xl mt-4">{interests.length}</p>
              <p className="overline mt-1">Applications</p>
            </button>
          </div>
        </div>
        <div className="bg-white p-6">
          <p className="overline mb-4 flex items-center gap-2"><Bell className="h-3.5 w-3.5" /> Notifications</p>
          {notes.length === 0 && <p className="text-sm text-muted-foreground">No notifications yet.</p>}
          <div className="space-y-px">
            {notes.slice(0, 8).map((n) => (
              <button key={n.id} onClick={() => markRead(n.id)} className={`w-full text-left p-3 border-b border-black/5 transition-colors ${n.is_read ? "opacity-50" : "hover:bg-black/5"}`}>
                <div className="flex items-start gap-2">
                  {!n.is_read && <span className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 shrink-0" />}
                  <div>
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.body}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
